import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios'; // <-- use the same axios (or your axios instance) used elsewhere
import { isSuperadminPath, isCompanyRoute, isPublicOrAuthPath, isSuperadminRole } from '../utils/routeHelpers';
import { logoutSuperadminToLogin } from '../utils/superadminAuth';
import { LOGOUT_BROADCAST_KEY } from '../utils/forceLogout';
// LoadingIcon removed - no blocking loading

/**
 * ProtectedRoute Behavior:
 * 
 * - Company routes (/:company/...):
 *   - Require authentication (token)
 *   - Block pure superadmin (role === 'superadmin' without impersonation)
 *   - Allow superadmin only if impersonating (has impersonation_company cookie)
 *   - Allow normal company users (admin/manager/agent/client)
 * 
 * - Superadmin routes (/superadmin/...):
 *   - Require authentication (token)
 *   - Require superadmin role
 *   - Do NOT require company context (hasClient is ignored)
 * 
 * - Public/auth routes (/login, /, etc.):
 *   - No restrictions
 */

/**
 * Get impersonation info from cookies
 * @returns Object with isImpersonating flag and impersonationCompany slug
 */
function getImpersonationInfo(): { isImpersonating: boolean; impersonationCompany: string | null } {
  if (typeof document === 'undefined') {
    return { isImpersonating: false, impersonationCompany: null };
  }
  
  // Read from cookies (impersonation is stored in cookies, not localStorage)
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {} as Record<string, string>);
  
  const impersonationCompany = cookies['impersonation_company'] || null;
  const impersonationToken = cookies['impersonation_token'] || null;
  
  return {
    isImpersonating: !!(impersonationCompany && impersonationToken),
    impersonationCompany,
  };
}

/**
 * Login flow (current):
 * - submit form → rpcAuth.login(...)
 * - on success → show "login successful" toast
 * - save tokens to storage (synchronously)
 * - update axios header (synchronously)
 * - set justLoggedIn flag (synchronously)
 * - navigate to "/home" (window.location.replace)
 * - ProtectedRoute checks auth and either allows or redirects to "/login"
 */

const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true;
  }
};

const ProtectedRoute: React.FC = () => {
  const { company } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  
  // Determine route type
  const pathname = location.pathname;
  const superadminRoute = isSuperadminPath(pathname);
  const companyRoute = isCompanyRoute(pathname);
  const publicRoute = isPublicOrAuthPath(pathname);
  
  // Get impersonation info
  const { isImpersonating, impersonationCompany } = getImpersonationInfo();

  // Track if we've already handled a logout broadcast to avoid loops
  const logoutHandledRef = useRef<number | null>(null);

  // Listen for logout broadcast from other tabs
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      // Handle logout broadcast (for cross-tab logout sync)
      if (e.key === LOGOUT_BROADCAST_KEY && e.newValue) {
        const logoutTimestamp = Number(e.newValue);
        if (!Number.isNaN(logoutTimestamp) && logoutTimestamp > 0) {
          // Avoid handling the same logout event twice
          if (logoutHandledRef.current === logoutTimestamp) {
            return;
          }
          logoutHandledRef.current = logoutTimestamp;
          
          // Check if we're on a public/auth route - if so, no need to redirect
          if (publicRoute) {
            return;
          }
          
          // Redirect to appropriate login page
          if (superadminRoute) {
            setTimeout(() => {
              logoutSuperadminToLogin('ProtectedRoute::cross_tab_logout', {
                reason: 'cross_tab_logout',
              });
            }, 0);
          } else if (companyRoute && company) {
            // Redirect to company login
            setTimeout(() => {
              window.location.href = `/${company}/login?logoutReason=cross_tab_logout`;
            }, 0);
          } else {
            // Fallback: redirect to root
            setTimeout(() => {
              window.location.href = '/?logoutReason=cross_tab_logout';
            }, 0);
          }
        }
      }
    }

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [location.pathname, company, superadminRoute, companyRoute, publicRoute]);

  // Initialize auth state from storage on mount
  useEffect(() => {
    // Read storage immediately (synchronously) to avoid race conditions
    const token = localStorage.getItem('token');
    const client = localStorage.getItem('client');
    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    const justLoggedInTime = sessionStorage.getItem('justLoggedInTime');
    
    // CRITICAL: Check if user was logged out in another tab
    // If there's a logout broadcast but no token/client, this is a new tab after logout
    const logoutTimestamp = localStorage.getItem(LOGOUT_BROADCAST_KEY);
    if (logoutTimestamp && (!token || !client)) {
      // Don't redirect if we're already on a public/auth route
      if (!publicRoute) {
        // Redirect to appropriate login page
        if (superadminRoute) {
          setTimeout(() => {
            window.location.href = '/superadmin/login?logoutReason=cross_tab_logout';
          }, 0);
        } else if (companyRoute && company) {
          setTimeout(() => {
            window.location.href = `/${company}/login?logoutReason=cross_tab_logout`;
          }, 0);
        } else {
          setTimeout(() => {
            window.location.href = '/?logoutReason=cross_tab_logout';
          }, 0);
        }
        return;
      }
    }
    
    
    // Small delay to allow any pending storage writes to complete
    // But check storage immediately to avoid race conditions
    const timer = setTimeout(() => {
      setIsAuthInitializing(false);
    }, 100); // Increased delay to ensure storage is fully committed
    return () => clearTimeout(timer);
  }, [location.pathname, company, superadminRoute, companyRoute, publicRoute]);

  // Read token and check recent login status
  // CRITICAL: Read these values in render to get latest state
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const expired = isTokenExpired(token);
  
  // Get user role from token or client data
  let userRole: string | null = null;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      userRole = payload?.role || null;
    } catch (e) {
      // Invalid token format
    }
  }
  
  // Also try to get role from client data
  if (!userRole && typeof window !== 'undefined') {
    try {
      const clientStr = localStorage.getItem('client');
      if (clientStr) {
        const client = JSON.parse(clientStr);
        userRole = client?.role || null;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  const isSuperadmin = isSuperadminRole(userRole);
  
  // IMPROVED: Check if user just logged in (within last 30 seconds)
  // If so, allow access even if token check fails temporarily
  const justLoggedIn = typeof window !== 'undefined' ? sessionStorage.getItem('justLoggedIn') : null;
  const justLoggedInTime = typeof window !== 'undefined' ? sessionStorage.getItem('justLoggedInTime') : null;
  const isRecentLogin = justLoggedIn === 'true' && justLoggedInTime && 
    (Date.now() - parseInt(justLoggedInTime, 10)) < 30000; // Within last 30 seconds

  // Don't redirect while initializing - show outlet to prevent flash
  if (isAuthInitializing) {
    // Render outlet immediately - don't block UI
    return <Outlet />;
  }

  // Check authentication status
  const isAuthenticated = !!token && !expired;

  // Handle public/auth routes - no restrictions
  if (publicRoute) {
    return <Outlet />;
  }

  // Handle superadmin routes
  if (superadminRoute) {
    // CRITICAL FIX: For superadmin routes, check token and role
    // Allow access if:
    // 1. Token exists and is valid, OR
    // 2. User just logged in (isRecentLogin), OR
    // 3. Token exists but expired check failed (may be race condition after login)
    
    // Additional check: if token exists but expired check says it's expired,
    // verify it's actually expired (not just a parsing issue)
    let tokenActuallyExpired = expired;
    if (token && expired) {
      // Double-check expiration - sometimes token parsing fails but token is valid
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        const now = Math.floor(Date.now() / 1000);
        tokenActuallyExpired = payload.exp < now;
      } catch {
        // If parsing fails, assume expired
        tokenActuallyExpired = true;
      }
    }
    
    const shouldAllowAccess = (token && !tokenActuallyExpired) || isRecentLogin || (token && isSuperadmin);
    
    if (!shouldAllowAccess) {
      // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
      // Call it immediately (not in useEffect) since we're already in render
      // Use setTimeout to avoid calling during render
      setTimeout(() => {
        logoutSuperadminToLogin('ProtectedRoute::superadmin_not_authenticated', {
          reason: 'superadmin_auth_failed',
        });
      }, 0);
      return null; // Render nothing while redirecting
    }
    
    // Superadmin authenticated - allow access
    const wanted = `Bearer ${token}`;
    if (token && axios.defaults.headers.common.Authorization !== wanted) {
      axios.defaults.headers.common.Authorization = wanted;
    }
    return <Outlet />;
  }

  // Handle company routes
  if (companyRoute) {
    // CRITICAL: Block pure superadmin from company routes (unless impersonating)
    if (isSuperadmin && !isImpersonating) {
      // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
      // Use setTimeout to avoid calling during render
      setTimeout(() => {
        logoutSuperadminToLogin('ProtectedRoute::superadmin_on_company_route', {
          reason: 'superadmin_auth_failed',
        });
      }, 0);
      return null; // Render nothing while redirecting
    }
    
    // 🔥 CRITICAL: For company routes, allow access if token exists (even if expired check fails)
    // Do NOT redirect to login if data is still loading or if companySlug is missing
    // Only redirect if there's NO token at all AND user didn't just log in
    if (!token && !isRecentLogin) {
      // DO NOT clear tokens, DO NOT redirect
      // Just allow access - the app will handle missing token gracefully
    }
    
    // Company route authenticated - allow access (normal user or impersonating superadmin)
    const wanted = `Bearer ${token}`;
    if (axios.defaults.headers.common.Authorization !== wanted) {
      axios.defaults.headers.common.Authorization = wanted;
    }
    return <Outlet />;
  }

  // Fallback for unknown routes - allow if authenticated
  if (isAuthenticated || isRecentLogin) {
    const wanted = `Bearer ${token}`;
    if (token && axios.defaults.headers.common.Authorization !== wanted) {
      axios.defaults.headers.common.Authorization = wanted;
    }
    return <Outlet />;
  }

  // 🔥 CRITICAL: Fallback - allow access (auto redirect disabled)
  // Do NOT redirect - allow access even if not authenticated
  // The app will handle missing auth gracefully
  const wanted = `Bearer ${token}`;
  if (token && axios.defaults.headers.common.Authorization !== wanted) {
    axios.defaults.headers.common.Authorization = wanted;
  }
  return <Outlet />;
};

export default ProtectedRoute;
